import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import {
  PATH_ORGANIZATION_BILLING,
  PATH_ORGANIZATION_BILLING_EDIT,
} from '../../app/constants/paths.constant';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { OrganizationForm } from '../../forms/ThirdParty/Organization';
import { Billing as BillingCmp } from '../../components/Billing';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const OrganizationPage_Billing_Organization_Fragment = graphql(`
  fragment OrganizationPage_Billing_Organization_Fragment on Organization {
    _id
    isInternal
    billReceiver {
      kind
      ... on Person {
        ...PersonCardCmp_Person_Fragment
      }
      ... on Organization {
        ...OrganizationCardCmp_Organization_Fragment
      }
    }
    billEmitters {
      _id
      kind
      ... on Person {
        ...PersonCardCmp_Person_Fragment
      }
      ... on Organization {
        ...OrganizationCardCmp_Organization_Fragment
      }
    }
    ...BillingCmp_ThirdParty_Fragment
    ...ThirdPartyForm_Organization_Fragment
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_Billing_Organization_Fragment
  >;
}

export const Billing = ({ organization: pOrganization }: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationPage_Billing_Organization_Fragment,
    pOrganization,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_ORGANIZATION_BILLING,
      edit: PATH_ORGANIZATION_BILLING_EDIT,
    },
    {
      id: organization._id,
    },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <BillingCmp thirdParty={organization} />
      <SpeedDial
        actions={[
          {
            id: 'editOrganizationBilling',
            icon: <EditIcon />,
            name: 'Modifier Infos Facturation',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations de Facturation de l'Organisation"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <OrganizationForm
          formVariant={FormVariant.Modification}
          initialValues={organization}
          isInternal={organization.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          organizationId={organization._id}
          stepIdsSubset={[
            ThirdPartyFormTab.BillEmitters,
            ThirdPartyFormTab.BillReceiver,
          ]}
          title="Formulaire de modification des Informations de Facturation de l'Organisation"
        />
      </RightDrawer>
    </>
  );
};
